// KidRecipeBook — Peezy's personal grimoire. Lists every item he's
// discovered with the ingredient formula that produced it. Tap a
// row to expand the formula. Tap "MAKE AGAIN" to refill the Mix
// grid with the same ingredients and switch back to the Mix tab.
//
// Source of truth:
//   kid_craft_inventory rows the kid owns
//   emoji_combinations rows that produced each (matched by
//     result_emoji + result_name)
//
// Decoding ingredient emojis → names: we walk KID_FOODS first
// (primitives), then KID_TECHNIQUES, then fall back to other
// kid_craft_inventory rows (chained discoveries are recognized).
//
// EMOJI-IN-UI EXCEPTION: this tree (src/experiments/kids-mode/**)
// is the explicit opt-in.

import { useEffect, useMemo, useState } from "react";
import { COLOR, FONT, RADIUS } from "../../lib/tokens";
import { supabase } from "../../lib/supabase";
import { KID_FOODS } from "./kidFoods";
import { KID_TECHNIQUES } from "./kidTools";
import { useKidFavorites } from "../../lib/useKidFavorites";
import { PinnedSearch } from "./PinnedSearch";

function craftSlug(name) {
  return "craft:" + (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60)
    || "craft:dish";
}

// Decode ingredients_key (sorted concat of emojis) by greedy-matching
// each known emoji in priority order. Emojis are variable-byte so
// we sort known emojis by length descending to match longer ones
// first (e.g. ⏲️ has a VS16 selector and would otherwise split).
function decodeIngredients(key, knownEmojiMap) {
  if (!key) return [];
  const knownByLen = Array.from(knownEmojiMap.keys()).sort((a, b) => b.length - a.length);
  const out = [];
  let i = 0;
  while (i < key.length) {
    let matched = false;
    for (const e of knownByLen) {
      if (key.startsWith(e, i)) {
        out.push({ emoji: e, name: knownEmojiMap.get(e) || e });
        i += e.length;
        matched = true;
        break;
      }
    }
    if (!matched) {
      // Skip one codepoint and continue — better than infinite loop.
      const codePoint = key.codePointAt(i) || 0;
      const skipLen = codePoint > 0xFFFF ? 2 : 1;
      out.push({ emoji: key.slice(i, i + skipLen), name: "?" });
      i += skipLen;
    }
  }
  return out;
}

export function KidRecipeBook({ userId, onClose, onMakeAgain }) {
  const [discoveries, setDiscoveries] = useState([]);
  const [recipesByKey, setRecipesByKey] = useState(new Map());
  const [imagesByKey, setImagesByKey] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [expandedKey, setExpandedKey] = useState(null);
  const [query, setQuery] = useState("");
  const [favsOnly, setFavsOnly] = useState(false);
  const favs = useKidFavorites(userId);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      // 1. Kid's discoveries
      const { data: inv, error: invErr } = await supabase
        .from("kid_craft_inventory")
        .select("item_emoji, item_name, discovered_at, is_first_discovery")
        .eq("kid_user_id", userId)
        .order("discovered_at", { ascending: false });
      if (invErr) {
        console.warn("[recipe-book] inventory:", invErr.message);
      }
      if (cancelled) return;

      const inventory = inv || [];

      // 2. Look up matching emoji_combinations rows so we know the
      // recipe formula for each discovery. Match on result_emoji
      // AND result_name. Bulk fetch is cheaper than per-row.
      const uniqueEmojis = Array.from(new Set(inventory.map((r) => r.item_emoji)));
      let recipes = [];
      if (uniqueEmojis.length > 0) {
        const { data: combos } = await supabase
          .from("emoji_combinations")
          .select("result_emoji, result_name, ingredients_key, c_emoji, c_name, image_url, first_discovered_by")
          .in("result_emoji", uniqueEmojis);
        recipes = combos || [];
      }

      if (cancelled) return;

      // Index recipes by (emoji|name) → first matching combo
      const byKey = new Map();
      const imgByKey = new Map();
      for (const r of recipes) {
        const k = `${r.result_emoji}|${r.result_name}`;
        if (!byKey.has(k)) byKey.set(k, r);
        if (r.image_url) imgByKey.set(k, r.image_url);
      }

      setDiscoveries(inventory);
      setRecipesByKey(byKey);
      setImagesByKey(imgByKey);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Emoji → name lookup map: primitives + techniques + discoveries
  const knownEmojiMap = useMemo(() => {
    const map = new Map();
    for (const f of KID_FOODS) {
      if (!map.has(f.emoji)) map.set(f.emoji, f.displayName);
    }
    for (const t of KID_TECHNIQUES) {
      if (!map.has(t.emoji)) map.set(t.emoji, t.displayName);
    }
    for (const d of discoveries) {
      if (!map.has(d.item_emoji)) map.set(d.item_emoji, d.item_name);
    }
    return map;
  }, [discoveries]);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    return discoveries
      .filter((d) => !q || (d.item_name || "").toLowerCase().includes(q))
      .filter((d) => !favsOnly || favs.isFavorite("recipe", craftSlug(d.item_name)));
  }, [discoveries, q, favsOnly, favs]);

  return (
    <div
      onClick={onClose}
      style={{
        // Full-screen solid backdrop — NO backdrop-filter blur.
        // backdrop-filter on a fixed parent breaks inner touch
        // scroll on iOS Safari + makes the sheet look transparent
        // when the user rotates the device (the blur layer
        // re-renders out of sync with the sheet).
        position: "fixed",
        inset: 0,
        background: "rgba(20,18,14,0.7)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        zIndex: 240,
        padding: 0,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 560,
          // Use small-viewport-height (svh) so rotation doesn't
          // recompute the sheet height mid-render. Falls back to
          // vh on older browsers.
          height: "94svh",
          maxHeight: "94vh",
          // Fully opaque solid fill (was a gradient — gradients
          // render slightly translucent on some iOS color-managed
          // displays). Plain color = guaranteed opaque.
          background: "#FFF8E7",
          borderRadius: "20px 20px 0 0",
          padding: "20px 0 calc(16px + env(safe-area-inset-bottom))",
          boxShadow: "0 -10px 32px rgba(0,0,0,0.35)",
          display: "flex",
          flexDirection: "column",
          // Hard isolation so internal scroll doesn't bubble to
          // the overlay or to the underlying Craft screen.
          overscrollBehavior: "contain",
          touchAction: "pan-y",
        }}
      >
        {/* Header */}
        <div style={{ padding: "0 20px 12px", flexShrink: 0 }}>
          <div style={{ fontSize: 40, lineHeight: 1, textAlign: "center", marginBottom: 4 }}>
            📖
          </div>
          <div
            style={{
              fontFamily: FONT.itemTitle,
              fontStyle: "italic",
              fontSize: 24,
              color: COLOR.ink,
              textAlign: "center",
              marginBottom: 4,
            }}
          >
            Recipe Book
          </div>
          <div
            style={{
              fontFamily: FONT.forma,
              fontSize: 12,
              color: COLOR.muted,
              textAlign: "center",
              marginBottom: 12,
            }}
          >
            {discoveries.length} {discoveries.length === 1 ? "discovery" : "discoveries"}
          </div>

          <div style={{ marginBottom: 8 }}>
            <PinnedSearch value={query} onChange={setQuery} placeholder="Search recipes…" />
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              onClick={() => setFavsOnly(false)}
              style={{
                flex: 1,
                padding: "8px 12px",
                background: !favsOnly ? COLOR.leaf : "transparent",
                color: !favsOnly ? "#fff" : COLOR.ink,
                border: `1px solid ${!favsOnly ? COLOR.leaf : COLOR.border}`,
                borderRadius: 999,
                fontFamily: FONT.mono,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.08em",
                cursor: "pointer",
              }}
            >
              ALL
            </button>
            <button
              type="button"
              onClick={() => setFavsOnly(true)}
              style={{
                flex: 1,
                padding: "8px 12px",
                background: favsOnly ? COLOR.rose : "transparent",
                color: favsOnly ? "#fff" : COLOR.ink,
                border: `1px solid ${favsOnly ? COLOR.rose : COLOR.border}`,
                borderRadius: 999,
                fontFamily: FONT.mono,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.08em",
                cursor: "pointer",
              }}
            >
              💖 FAVORITES
            </button>
          </div>
        </div>

        {/* Recipe list */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            padding: "4px 16px 16px",
          }}
        >
          {loading ? (
            <div style={{ padding: "30px 20px", textAlign: "center", color: COLOR.muted, fontFamily: FONT.forma }}>
              Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div
              style={{
                padding: "40px 20px",
                textAlign: "center",
                fontFamily: FONT.forma,
                fontSize: 14,
                color: COLOR.muted,
              }}
            >
              <div style={{ fontSize: 60, lineHeight: 1, marginBottom: 12, opacity: 0.5 }}>
                {discoveries.length === 0 ? "🥚" : "🔍"}
              </div>
              {discoveries.length === 0
                ? "Go to Mix and combine some ingredients to start your book!"
                : "No recipes match."}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {filtered.map((d) => {
                const k = `${d.item_emoji}|${d.item_name}`;
                const recipe = recipesByKey.get(k);
                const expanded = expandedKey === k;
                const imageUrl = imagesByKey.get(k);
                const ingredients = recipe
                  ? decodeIngredients(recipe.ingredients_key, knownEmojiMap)
                  : [];
                const slug = craftSlug(d.item_name);
                const favored = favs.isFavorite("recipe", slug);
                return (
                  <RecipeRow
                    key={k}
                    discovery={d}
                    recipe={recipe}
                    ingredients={ingredients}
                    imageUrl={imageUrl}
                    expanded={expanded}
                    favored={favored}
                    onToggleFavorite={() => favs.toggle({
                      kind:  "recipe",
                      id:    slug,
                      name:  d.item_name,
                      emoji: d.item_emoji,
                    })}
                    onToggle={() => setExpandedKey(expanded ? null : k)}
                    onMakeAgain={() => {
                      onMakeAgain?.({ ingredients, technique: recipe?.c_emoji ? { emoji: recipe.c_emoji, name: recipe.c_name || "" } : null });
                      onClose();
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>

        <div style={{ padding: "10px 20px 0", flexShrink: 0 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: "100%",
              padding: 14,
              background: COLOR.leaf,
              color: "#fff",
              border: "none",
              borderRadius: 10,
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.08em",
              cursor: "pointer",
            }}
          >
            CLOSE
          </button>
        </div>
      </div>
    </div>
  );
}

function RecipeRow({ discovery, recipe, ingredients, imageUrl, expanded, favored, onToggle, onMakeAgain, onToggleFavorite }) {
  const hasImage = !!imageUrl;
  return (
    <div
      style={{
        background: "#fff",
        border: `1.5px solid ${discovery.is_first_discovery ? COLOR.gold : COLOR.border}`,
        borderRadius: 14,
        padding: 12,
        boxShadow: discovery.is_first_discovery
          ? `0 2px 10px ${COLOR.goldDeep}`
          : "0 1px 4px rgba(0,0,0,0.05)",
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        style={{
          all: "unset",
          cursor: "pointer",
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 12,
          touchAction: "manipulation",
        }}
      >
        {hasImage ? (
          <div
            style={{
              width: 56,
              height: 56,
              background: `url(${imageUrl}) center / cover no-repeat`,
              borderRadius: 12,
              flexShrink: 0,
              border: `1px solid ${COLOR.border}`,
            }}
          />
        ) : (
          <div
            style={{
              width: 56,
              height: 56,
              fontSize: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: COLOR.deep,
              borderRadius: 12,
              flexShrink: 0,
            }}
          >
            {discovery.item_emoji}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
          <div
            style={{
              fontFamily: FONT.itemTitle,
              fontStyle: "italic",
              fontSize: 18,
              color: COLOR.ink,
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {discovery.item_name}
          </div>
          {discovery.is_first_discovery && (
            <div
              style={{
                fontFamily: FONT.mono,
                fontSize: 9,
                letterSpacing: "0.12em",
                fontWeight: 700,
                color: COLOR.gold,
                marginTop: 2,
              }}
            >
              ⭐ FIRST DISCOVERY
            </div>
          )}
        </div>
        <span
          onClick={(e) => { e.stopPropagation(); onToggleFavorite?.(); }}
          style={{
            fontSize: 24,
            cursor: "pointer",
            padding: "4px 8px",
            lineHeight: 1,
          }}
          aria-label={favored ? "unfavorite" : "favorite"}
          role="button"
        >
          {favored ? "💖" : "🤍"}
        </span>
        <span style={{ fontSize: 18, color: COLOR.muted }}>
          {expanded ? "▾" : "▸"}
        </span>
      </button>

      {expanded && (
        <div
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: `1px solid ${COLOR.border}`,
          }}
        >
          {recipe ? (
            <>
              <div
                style={{
                  fontFamily: FONT.mono,
                  fontSize: 9,
                  letterSpacing: "0.14em",
                  fontWeight: 700,
                  color: COLOR.sub,
                  marginBottom: 6,
                }}
              >
                RECIPE
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 10,
                }}
              >
                {ingredients.map((ing, idx) => (
                  <span key={idx} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "5px 10px",
                        background: COLOR.soil,
                        border: `1px solid ${COLOR.border}`,
                        borderRadius: 999,
                        fontFamily: FONT.forma,
                        fontSize: 12,
                        fontWeight: 600,
                        color: COLOR.ink,
                      }}
                    >
                      <span style={{ fontSize: 16 }}>{ing.emoji}</span>
                      {ing.name}
                    </span>
                    {idx < ingredients.length - 1 && (
                      <span style={{ color: COLOR.muted, fontFamily: FONT.itemTitle, fontStyle: "italic" }}>+</span>
                    )}
                  </span>
                ))}
                {recipe.c_emoji && (
                  <>
                    <span style={{ color: COLOR.muted, fontFamily: FONT.itemTitle, fontStyle: "italic" }}>↓</span>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "5px 10px",
                        background: COLOR.goldDeep,
                        border: `1px solid ${COLOR.goldDim}`,
                        borderRadius: 999,
                        fontFamily: FONT.forma,
                        fontSize: 12,
                        fontWeight: 600,
                        color: COLOR.gold,
                      }}
                    >
                      <span style={{ fontSize: 16 }}>{recipe.c_emoji}</span>
                      {recipe.c_name || "Cook"}
                    </span>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={onMakeAgain}
                className="mcm-focusable"
                style={{
                  width: "100%",
                  padding: 12,
                  background: COLOR.leaf,
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  fontFamily: FONT.mono,
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  cursor: "pointer",
                  boxShadow: `0 4px 10px ${COLOR.leafDim}`,
                }}
              >
                🧪 MAKE AGAIN
              </button>
            </>
          ) : (
            <div
              style={{
                fontFamily: FONT.forma,
                fontSize: 12,
                color: COLOR.muted,
                fontStyle: "italic",
              }}
            >
              Recipe details not available.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default KidRecipeBook;
